// src/App.js
import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase, SUPABASE_ANON_KEY } from './supabaseClient'
import { C, st, MODULES, BRAND, formatDateTime, getLabel } from './theme'
import { AppPrefsProvider, useAppPrefs } from './AppPrefs'
import { t } from './i18n'
import { PageHeader, EmptyState, StatCard, Pagination, PAGE_SIZE } from './components/UI'
import WMS from './modules/wms/WMS'
import Production from './modules/production/Production'
import Shipping from './modules/shipping/Shipping'
import CRM from './modules/crm/CRM'
import Reports from './modules/reports/Reports'
import Maintenance from './modules/maintenance/Maintenance'
import FiveS from './modules/fives/FiveS'
import Tasks from './modules/tasks/Tasks'
import Purchasing from './modules/purchasing/Purchasing'
import Products from './modules/products/Products'
import Attendance from './modules/attendance/Attendance'
import SuperAdminPortal from './components/SuperAdmin'
import PositionManager from './components/PositionManager'
import EmployeeManager from './components/EmployeeManager'
import UserSettings from './components/UserSettings'
import PublicDashboard from './components/PublicDashboard'
import AIAssistant from './components/AIAssistant'
import { downloadFullBackup } from './utils/backup'

const POSITIONS = [
  { key: 'admin', label: '系統管理員', en: 'System Admin', color: '#3d8ef0' },
  { key: 'chairman', label: '董事長', en: 'Chairman', color: '#7c3aed' },
  { key: 'ceo', label: '總經理', en: 'CEO', color: '#6d28d9' },
  { key: 'vice_ceo', label: '副總經理', en: 'Vice CEO', color: '#5b21b6' },
  { key: 'factory_manager', label: '廠長', en: 'Factory Manager', color: '#c084fc' },
  { key: 'senior_manager', label: '高階主管', en: 'Senior Manager', color: '#a78bfa' },
  { key: 'section_chief', label: '課長', en: 'Section Chief', color: '#60a5fa' },
  { key: 'team_leader', label: '組長', en: 'Team Leader', color: '#34d399' },
  { key: 'line_leader', label: '車長', en: 'Line Leader', color: '#4ade80' },
  { key: 'warehouse', label: '倉儲人員', en: 'Warehouse Staff', color: '#38bdf8' },
  { key: 'sales', label: '業務', en: 'Sales', color: '#fbbf24' },
  { key: 'sales_assistant', label: '業務助理', en: 'Sales Assistant', color: '#f59e0b' },
  { key: 'accountant', label: '會計', en: 'Accountant', color: '#f472b6' },
  { key: 'driver', label: '司機', en: 'Driver', color: '#94a3b8' },
  { key: 'consultant', label: '顧問', en: 'Consultant', color: '#6b7280' },
  { key: 'user', label: '一般使用者', en: 'General User', color: '#64748b' },
]

const PERMISSION_KEYS = [
  { key: 'wms', label: '倉儲管理', en: 'WMS', icon: '📦' },
  { key: 'production', label: '生產管理', en: 'MES', icon: '🏭' },
  { key: 'shipping', label: '出貨管理', en: 'SHP', icon: '🚚' },
  { key: 'crm', label: '客戶管理', en: 'CRM', icon: '👥' },
  { key: 'reports', label: '報表分析', en: 'RPT', icon: '📊' },
  { key: 'delete_records', label: '刪除紀錄', en: 'Delete Records', icon: '🗑' },
  { key: 'delete_products', label: '刪除商品', en: 'Delete Products', icon: '❌' },
  { key: 'manage_users', label: '管理使用者', en: 'Manage Users', icon: '👑' },
  { key: 'tasks', label: '任務管理', en: 'Task Management', icon: '📋' },
  { key: 'assign', label: '派發權限', en: 'Assign Tasks/5S', icon: '📤' },
]

const DEFAULT_PERMISSIONS = {
  admin:           ['wms','production','shipping','crm','reports','maintenance','fives','tasks','purchasing','products','attendance','delete_records','delete_products','manage_users','assign'],
  chairman:        ['wms','production','shipping','crm','reports','maintenance','fives','tasks','purchasing','products','attendance','manage_users','assign'],
  ceo:             ['wms','production','shipping','crm','reports','maintenance','fives','tasks','purchasing','products','attendance','manage_users','assign'],
  vice_ceo:        ['wms','production','shipping','crm','reports','maintenance','fives','tasks','purchasing','products','attendance','manage_users','assign'],
  factory_manager: ['wms','production','shipping','crm','reports','maintenance','fives','tasks','purchasing','products','attendance','delete_records','delete_products','manage_users','assign'],
  senior_manager:  ['wms','production','shipping','crm','reports','maintenance','fives','tasks','purchasing','products','attendance','delete_records','manage_users','assign'],
  section_chief:   ['wms','production','shipping','reports','maintenance','fives','tasks','purchasing','products','attendance','manage_users','assign'],
  team_leader:     ['wms','production','shipping','reports','maintenance','fives','tasks','products','attendance','manage_users','assign'],
  line_leader:     ['wms','production','maintenance','fives','tasks','attendance','manage_users'],
  warehouse:       ['wms','reports','fives','tasks','products','attendance'],
  sales:           ['shipping','crm','reports','tasks','products','attendance'],
  sales_assistant: ['shipping','crm','wms','tasks','products','attendance'],
  accountant:      ['reports','wms','purchasing','attendance'],
  driver:          ['shipping','tasks','attendance'],
  consultant:      ['tasks','attendance'],
  user:            ['wms','fives','tasks','attendance'],
}

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])
  return isMobile
}

function BottomNav({ activeTab, setActiveTab, enabledModules, lockedModules = [], isAdmin, lowStockCount }) {
  const { lang } = useAppPrefs()
  const allModules = MODULES.filter(m => enabledModules.includes(m.key) || lockedModules.includes(m.key))
  const navModules = allModules.slice(0, 3)
  const moreCount = allModules.slice(3).length   // 放在「更多」裡的模組數
  const items = [
    { key: 'dashboard', label: t(lang, 'navDashboard'), icon: '🏠' },
    ...navModules.map(m => ({ ...m, label: getLabel(m, lang) })),
    { key: 'more', label: t(lang, 'navMore'), icon: '☰', badge: moreCount > 0 ? moreCount : null },
  ]
  return (
    <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: C.panel, borderTop: `1.5px solid ${C.border}`, display: 'flex', zIndex: 200, paddingBottom: 'env(safe-area-inset-bottom)', boxShadow: '0 -2px 12px rgba(0,0,0,0.07)' }}>
      {items.map(item => {
        const isLocked = lockedModules.includes(item.key)
        const isActive = activeTab === item.key || (!isLocked && activeTab === `__locked__${item.key}`)
        const icon = isLocked ? '🔒' : item.icon
        return (
          <button key={item.key} onClick={() => setActiveTab(isLocked ? `__locked__${item.key}` : item.key)}
            style={{ flex: 1, background: 'none', border: 'none', padding: '6px 2px 4px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, position: 'relative', opacity: isLocked ? 0.5 : 1 }}>
            {/* Active top bar */}
            {isActive && <div style={{ position: 'absolute', top: 0, left: '20%', right: '20%', height: 2.5, background: C.accent, borderRadius: '0 0 3px 3px' }} />}
            {/* Icon pill */}
            <div style={{ width: 46, height: 32, borderRadius: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', background: isActive ? `${C.accent}18` : 'transparent', transition: 'background 0.15s', position: 'relative' }}>
              <span style={{ fontSize: 20 }}>{icon}</span>
              {/* WMS 庫存警告 */}
              {item.key === 'wms' && lowStockCount > 0 && !isLocked && (
                <div style={{ position: 'absolute', top: 1, right: 2, width: 15, height: 15, background: C.red, borderRadius: '50%', fontSize: 8, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, border: '1.5px solid #fff' }}>{lowStockCount > 9 ? '9+' : lowStockCount}</div>
              )}
              {/* 更多 badge */}
              {item.badge && (
                <div style={{ position: 'absolute', top: 1, right: 2, minWidth: 15, height: 15, background: C.accent, borderRadius: 8, fontSize: 8, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, padding: '0 3px', border: '1.5px solid #fff' }}>{item.badge}</div>
              )}
            </div>
            <span style={{ fontSize: 10, color: isActive ? C.accent : isLocked ? C.yellow : C.muted, fontWeight: isActive ? 700 : 400, lineHeight: 1 }}>{item.label}</span>
          </button>
        )
      })}
    </div>
  )
}

function Sidebar({ activeTab, setActiveTab, userProfile, realtimeStatus, onLogout, lowStockCount, enabledModules, lockedModules = [], onOpenSettings }) {
  const isAdmin = userProfile?.role === 'admin'
  const isSuperAdmin = userProfile?.is_super_admin
  const { lang } = useAppPrefs()
  const _ = key => t(lang, key)
  const navItems = [
    { key: 'dashboard', label: _('navDashboard'), icon: '🏠' },
    ...MODULES.filter(m => enabledModules.includes(m.key) || lockedModules.includes(m.key))
              .map(m => ({ ...m, label: getLabel(m, lang) })),
    ...(isAdmin ? [
      { key: 'positions', label: _('navPositions'), icon: '👔' },
      { key: 'users',     label: _('navUsers'),     icon: '👥' },
      { key: 'notify',    label: _('navNotify'),    icon: '📣' },
      { key: 'logs',      label: _('navLogs'),      icon: '🗒️' },
    ] : []),
    ...(!isAdmin && enabledModules.includes('manage_users') ? [
      { key: 'positions', label: _('navPositions'), icon: '👔' },
      { key: 'users',     label: _('navUsers'),     icon: '👥' },
    ] : []),
  ]
  return (
    <div style={{ width: C.sidebar, background: C.sidebarBg, display: 'flex', flexDirection: 'column', position: 'fixed', top: 0, left: 0, height: '100vh', zIndex: 100, overflowY: 'auto' }}>
      {/* Logo */}
      <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <div style={{ fontSize: 15, fontWeight: 900, color: '#ffffff', letterSpacing: 0.5 }}>{BRAND.icon} {BRAND.short}</div>
        <div style={{ fontSize: 12, color: C.sidebarMuted, marginTop: 4, lineHeight: 1.4 }}>{BRAND.name}</div>
      </div>

      {/* User info */}
      <div style={{ padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ width: 38, height: 38, borderRadius: '50%', background: C.sidebarActive, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0, fontWeight: 700, color: '#fff' }}>
          {(userProfile?.name || '?')[0].toUpperCase()}
        </div>
        <div style={{ overflow: 'hidden', flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#ffffff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{userProfile?.name}</div>
          <div style={{ fontSize: 11, color: C.sidebarMuted, marginTop: 2 }}>
            {isSuperAdmin ? '👑 Super Admin' : isAdmin ? '⚙️ 公司管理員' : userProfile?.role === 'manager' ? '👔 主管' : `👤 ${userProfile?.position || '一般用戶'}`}
          </div>
        </div>
        <button onClick={onOpenSettings} title="個人設定" style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: C.sidebarMuted, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.15s' }}>
          ⚙️
        </button>
      </div>

      {/* Realtime status */}
      <div style={{ padding: '8px 16px 4px' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, padding: '4px 10px', borderRadius: 20, background: realtimeStatus === 'connected' ? 'rgba(22,101,52,0.4)' : 'rgba(146,64,14,0.4)', color: realtimeStatus === 'connected' ? '#86efac' : '#fcd34d' }}>
          {realtimeStatus === 'connected' ? '🟢 即時同步' : '⏳ 連線中...'}
        </span>
      </div>

      {/* Nav items */}
      <div style={{ padding: '8px 10px', flex: 1 }}>
        {navItems.map(item => {
          const active = activeTab === item.key
          const isSuperItem = item.key === 'super_admin'
          const isLocked = lockedModules.includes(item.key)
          return (
            <div key={item.key}
              onClick={() => setActiveTab(isLocked ? `__locked__${item.key}` : item.key)}
              style={{ padding: '12px 14px', borderRadius: 10, marginBottom: 4, cursor: 'pointer', opacity: isLocked ? 0.55 : 1,
                background: active ? (isSuperItem ? 'rgba(245,158,11,0.3)' : C.sidebarActive) : isLocked ? 'rgba(255,255,255,0.04)' : 'transparent',
                border: active ? 'none' : isLocked ? '1px dashed rgba(255,255,255,0.15)' : isSuperItem ? '1px solid rgba(245,158,11,0.3)' : '1px solid transparent',
                display: 'flex', alignItems: 'center', gap: 12, transition: 'all 0.15s' }}>
              <span style={{ fontSize: 18, flexShrink: 0 }}>{item.icon}</span>
              <div style={{ flex: 1, overflow: 'hidden' }}>
                <div style={{ fontSize: 14, fontWeight: active ? 700 : 500, color: active ? '#ffffff' : isSuperItem ? '#fbbf24' : C.sidebarText }}>{item.label}</div>
                {isLocked
                  ? <div style={{ fontSize: 10, color: 'rgba(250,204,21,0.7)', marginTop: 1 }}>🔒 需升級方案</div>
                  : (item.en || item.desc) && <div style={{ fontSize: 10, color: C.sidebarMuted, marginTop: 1 }}>{item.en || item.desc}</div>
                }
              </div>
              {item.key === 'wms' && lowStockCount > 0 && !isLocked && (
                <span style={{ background: C.redBg, color: C.red, borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>{lowStockCount}</span>
              )}
              {isLocked && <span style={{ fontSize: 14 }}>🔒</span>}
            </div>
          )
        })}
      </div>

      <button onClick={onLogout} style={{ margin: '0 10px 16px', padding: '12px', borderRadius: 10, background: 'rgba(239,68,68,0.15)', border: '1.5px solid rgba(239,68,68,0.4)', color: '#fca5a5', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
        ⏏ 登出 · Sign Out
      </button>
    </div>
  )
}

// ── Notification Bell ────────────────────────────────────────────────────────
function NotificationBell({ count, notifications, onRead, onReadAll, show, setShow, isMobile = false }) {
  const NotifList = () => (
    <>
      <div style={{ padding: '12px 16px 10px', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <span style={{ fontSize: 15, fontWeight: 800, color: '#f1f5f9' }}>🔔 通知</span>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {count > 0 && <button onClick={onReadAll} style={{ fontSize: 12, color: '#60a5fa', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>全部已讀</button>}
          {isMobile && <button onClick={() => setShow(false)} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '50%', width: 28, height: 28, color: '#94a3b8', fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>}
        </div>
      </div>
      <div style={{ overflowY: 'auto', flex: 1 }}>
        {notifications.length === 0 ? (
          <div style={{ padding: 28, textAlign: 'center', color: '#64748b', fontSize: 13 }}>暫無通知</div>
        ) : notifications.map(n => (
          <div key={n.id} onClick={() => onRead(n.id)} style={{ padding: '12px 16px', borderBottom: '1px solid #1e293b22', cursor: 'pointer', background: n.is_read ? 'transparent' : 'rgba(59,130,246,0.08)' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              {!n.is_read && <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#3b82f6', flexShrink: 0, marginTop: 5 }} />}
              <div style={{ flex: 1, marginLeft: n.is_read ? 17 : 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 2 }}>
                  <span style={{ fontSize: 13, fontWeight: n.is_read ? 500 : 700, color: '#f1f5f9' }}>{n.title}</span>
                  {n.source && (
                    <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 20, fontWeight: 700,
                      background: n.source === '系統通知' ? 'rgba(251,191,36,0.18)' : 'rgba(99,102,241,0.18)',
                      color: n.source === '系統通知' ? '#fbbf24' : '#a5b4fc',
                      border: `1px solid ${n.source === '系統通知' ? 'rgba(251,191,36,0.35)' : 'rgba(99,102,241,0.35)'}`,
                      whiteSpace: 'nowrap',
                    }}>{n.source === '系統通知' ? '🛠 系統' : `👤 ${n.source}`}</span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: '#94a3b8' }}>{n.body}</div>
                <div style={{ fontSize: 10, color: '#475569', marginTop: 4 }}>{new Date(n.created_at).toLocaleString('zh-TW')}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  )

  return (
    <div style={{ position: 'relative' }}>
      <button onClick={() => setShow(s => !s)}
        style={{ position: 'relative', width: 36, height: 36, borderRadius: '50%', background: show ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.1)', border: '1.5px solid rgba(255,255,255,0.2)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>
        🔔
        {count > 0 && (
          <span style={{ position: 'absolute', top: -4, right: -4, background: '#ef4444', color: '#fff', borderRadius: '50%', width: 18, height: 18, fontSize: 10, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #1e293b' }}>
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      {/* 手機：底部全屏面板 */}
      {show && isMobile && (
        <>
          <div onClick={() => setShow(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 500 }} />
          <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: '#1e293b', borderRadius: '20px 20px 0 0', zIndex: 501, maxHeight: '75vh', display: 'flex', flexDirection: 'column', paddingBottom: 'env(safe-area-inset-bottom)', boxShadow: '0 -8px 32px rgba(0,0,0,0.4)' }}>
            <div style={{ width: 36, height: 4, background: '#475569', borderRadius: 2, margin: '12px auto 0', flexShrink: 0 }} />
            <NotifList />
          </div>
        </>
      )}

      {/* 桌機：右側下拉 */}
      {show && !isMobile && (
        <div style={{ position: 'absolute', top: 44, right: 0, width: 320, maxHeight: 440, background: '#1e293b', border: '1.5px solid #334155', borderRadius: 14, boxShadow: '0 8px 32px rgba(0,0,0,0.4)', zIndex: 999, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <NotifList />
        </div>
      )}
    </div>
  )
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  return new Uint8Array([...rawData].map(c => c.charCodeAt(0)))
}

export default function App() {
  // 公開儀表板 token 偵測（不需登入）
  const publicToken = new URLSearchParams(window.location.search).get('token')
  if (publicToken) return <PublicDashboard token={publicToken} />

  // QR 掃描 URL 參數：頁面一開啟就存入 sessionStorage（登入前也能保存）
  ;(() => {
    const p = new URLSearchParams(window.location.search)
    if (p.get('scan') && (p.get('pid') || p.get('lid'))) {
      sessionStorage.setItem('ypfops_qr', JSON.stringify({ pid: p.get('pid') || null, lid: p.get('lid') || null }))
      window.history.replaceState({}, '', window.location.pathname)
    }
  })()

  const isMobile = useIsMobile()
  const [session, setSession] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [pendingScan, setPendingScan] = useState(null)  // QR 掃描待處理：{ pid, lid }
  const [loginForm, setLoginForm] = useState({ email: '', password: '', companyCode: '' })
  const [loginError, setLoginError] = useState('')
  const [loginMode, setLoginMode] = useState(null)   // null | 'business' | 'employee'
  const [showPw, setShowPw] = useState(false)
  const [forgotPwMode, setForgotPwMode] = useState(false)
  const [forgotPwEmail, setForgotPwEmail] = useState('')
  const [forgotPwSent, setForgotPwSent] = useState(false)
  const [products, setProducts] = useState([])
  const [records, setRecords] = useState([])
  const [logs, setLogs] = useState([])
  const [users, setUsers] = useState([])
  const [pendingUsers, setPendingUsers] = useState([])
  const [enabledModules, setEnabledModules] = useState([])
  const [lockedModules, setLockedModules] = useState([])  // 有個人權限但公司未購買→飢餓行銷
  const [activeTab, setActiveTab] = useState('dashboard')
  const [toast, setToast] = useState(null)
  const [loading, setLoading] = useState(true)
  const [realtimeStatus, setRealtimeStatus] = useState('connecting')
  const [logPage, setLogPage] = useState(1)
  const [logSearch, setLogSearch] = useState('')
  const [notifyForm, setNotifyForm] = useState({ title: '', body: '', targetUserId: 'all' })
  const [sendingNotify, setSendingNotify] = useState(false)
  const [userModules, setUserModules] = useState({})
  const [notifications, setNotifications] = useState([])
  const [showNotifPanel, setShowNotifPanel] = useState(false)
  const [maintenanceInfo, setMaintenanceInfo] = useState(null)
  const [maintenanceNotices, setMaintenanceNotices] = useState([])
  const [dismissedNotices, setDismissedNotices] = useState([])
  const [backupState, setBackupState] = useState(null) // null | { current, total, name }
  const [showSettings, setShowSettings] = useState(false)
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(
    () => window.location.hash.includes('type=recovery')
  )
  const pushSetupDone = useRef(false)

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3500) }
  const addLog = useCallback(async (action, detail) => {
    await supabase.from('operation_logs').insert({
      action,
      detail,
      operator: userProfile?.name || session?.user?.email,
      user_name: userProfile?.name || session?.user?.email,
      company_id: userProfile?.company_id || null,
    })
  }, [userProfile, session])

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => setSession(session))
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, s) => {
      setSession(s)
      if (event === 'PASSWORD_RECOVERY') setIsPasswordRecovery(true)
    })
    return () => subscription.unsubscribe()
  }, [])

  useEffect(() => {
    if (session) {
      if (isPasswordRecovery) {
        setLoading(false)  // 直接解除 loading，讓重設畫面顯示
      } else {
        loadUserProfile()
      }
    }
    else { setUserProfile(null); setLoading(false) }
  }, [session, isPasswordRecovery])

  const loadUserProfile = async () => {
    const { data, error } = await supabase.from('user_profiles').select('*').eq('id', session.user.id).single()
    if (error || !data) {
      await supabase.auth.signOut()
      return
    }
    if (data.approved === false) { await supabase.auth.signOut(); return }

    // Check company status (skip for super admin)
    if (data.company_id && !data.is_super_admin) {
      const { data: company } = await supabase.from('companies').select('is_active, maintenance_mode, maintenance_message, name').eq('id', data.company_id).single()
      if (company) {
        if (!company.is_active) {
          await supabase.auth.signOut()
          setLoginError(`「${company.name}」帳號已被停用，請聯繫系統管理員`)
          return
        }
        if (company.maintenance_mode) {
          setMaintenanceInfo({ name: company.name, message: company.maintenance_message || '系統維護中，請稍後再試' })
        }
      }
    }

    setUserProfile(data)
    loadEnabledModules(session.user.id, data)
    setLoading(false)
    setupPushNotifications(session.user.id)

    // 偵測 QR 掃描（含登入前就掃碼的情境，已存入 sessionStorage）
    const savedQR = sessionStorage.getItem('ypfops_qr')
    if (savedQR) {
      sessionStorage.removeItem('ypfops_qr')
      try {
        const scanData = JSON.parse(savedQR)
        if (scanData.pid || scanData.lid) {
          setPendingScan(scanData)
          setActiveTab('wms')
        }
      } catch (_) {}
    }
  }

  const setupPushNotifications = async (userId) => {
    if (pushSetupDone.current) return
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return
    try {
      const reg = await navigator.serviceWorker.register('/sw.js')
      await navigator.serviceWorker.ready
      const existing = await reg.pushManager.getSubscription()
      let subscription = existing
      if (!subscription) {
        const permission = await Notification.requestPermission()
        if (permission !== 'granted') return
        const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY
        subscription = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey),
        })
      }
      await supabase.from('push_subscriptions').upsert({
        user_id: userId,
        subscription: subscription.toJSON(),
        user_agent: navigator.userAgent.slice(0, 200),
      }, { onConflict: 'user_id,subscription' })
      pushSetupDone.current = true
    } catch (err) {
      console.warn('Push setup:', err.message)
    }
  }

  const loadEnabledModules = async (userId, profile) => {
    const MODULE_PERM_KEYS = ['wms','production','shipping','crm','reports','maintenance','fives','tasks','purchasing','products','attendance']

    if ((profile?.role === 'admin' || (profile?.role === 'manager' && !profile?.position_id)) && profile?.company_id) {
      // 管理員 / 未指定職位的主管：依公司已購模組顯示，無鎖定
      const { data } = await supabase.from('company_modules').select('module').eq('company_id', profile.company_id).eq('enabled', true)
      if (data) setEnabledModules(data.map(d => d.module))
      setLockedModules([])
    } else {
      // 一般員工 / 有職位的主管：個人權限 ∩ 公司已購 = 實際可用
      // 個人有權限但公司未購 = 飢餓行銷鎖定
      const [{ data: perms }, { data: companyMods }] = await Promise.all([
        supabase.from('module_permissions').select('module').eq('user_id', userId).eq('enabled', true),
        profile?.company_id
          ? supabase.from('company_modules').select('module').eq('company_id', profile.company_id).eq('enabled', true)
          : { data: [] },
      ])
      const permKeys = perms?.map(d => d.module) || []
      const companyKeys = companyMods?.map(d => d.module) || []
      // 功能模組：取交集
      const accessible = permKeys.filter(k => MODULE_PERM_KEYS.includes(k) && companyKeys.includes(k))
      // 有個人授權但公司未購買 → 鎖定（飢餓行銷）
      const locked = permKeys.filter(k => MODULE_PERM_KEYS.includes(k) && !companyKeys.includes(k))
      // 特殊權限（非模組類）直接保留
      const specialPerms = permKeys.filter(k => !MODULE_PERM_KEYS.includes(k))
      setEnabledModules([...accessible, ...specialPerms])
      setLockedModules(locked)
    }
  }

  const loadNotifications = useCallback(async () => {
    if (!session) return
    const { data } = await supabase.from('notifications')
      .select('*').eq('user_id', session.user.id)
      .order('created_at', { ascending: false }).limit(30)
    if (data) setNotifications(data)
  }, [session])

  useEffect(() => {
    if (!session) return
    loadNotifications()
    const ch = supabase.channel('notif-' + session.user.id)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${session.user.id}` },
        (payload) => setNotifications(prev => [payload.new, ...prev].slice(0, 30))
      ).subscribe()
    return () => supabase.removeChannel(ch)
  }, [session, loadNotifications])

  const loadData = useCallback(async () => {
    if (!session) return
    const cid = userProfile?.company_id
    let pQ = supabase.from('products').select('*').order('created_at').limit(1000)
    let rQ = supabase.from('records').select('*, products(name, unit)').order('created_at', { ascending: false }).limit(500)
    if (cid) { pQ = pQ.eq('company_id', cid); rQ = rQ.eq('company_id', cid) }
    const [{ data: p }, { data: r }] = await Promise.all([pQ, rQ])
    if (p) setProducts(p)
    if (r) setRecords(r)
  }, [session, userProfile?.company_id])

  const loadLogs = useCallback(async () => {
    const cid = userProfile?.company_id
    let q = supabase.from('operation_logs').select('*').order('created_at', { ascending: false }).limit(500)
    if (cid) q = q.eq('company_id', cid)
    const { data, error } = await q
    // 即使 error 也 setLogs([]) 確保舊資料不殘留
    setLogs(data || [])
    if (error) console.error('loadLogs error:', error.message)
  }, [userProfile?.company_id])

  const loadUsers = useCallback(async () => {
    const cid = userProfile?.company_id
    let q = supabase.from('user_profiles').select('*').order('created_at')
    if (cid) q = q.eq('company_id', cid)
    const { data } = await q
    if (data) {
      setUsers(data.filter(u => u.approved !== false))
      setPendingUsers(data.filter(u => u.approved === false))
    }
  }, [userProfile?.company_id])

  // 載入維護排程通知（自動顯示橫幅）
  useEffect(() => {
    if (!session || !userProfile?.company_id) return
    const loadMaintenance = async () => {
      const { data } = await supabase
        .from('maintenance_schedules')
        .select('*')
        .or(`company_id.eq.${userProfile.company_id},company_id.is.null`)
        .not('status', 'in', '(cancelled,completed)')
        .order('starts_at')
      if (data) setMaintenanceNotices(data)
    }
    loadMaintenance()
  }, [session, userProfile?.company_id])

  useEffect(() => { if (session) { loadData() } }, [session, loadData])
  useEffect(() => { if (session && activeTab === 'logs') loadLogs() }, [session, activeTab, loadLogs])
  useEffect(() => { if (session && (activeTab === 'users' || activeTab === 'approvals')) loadUsers() }, [session, activeTab, loadUsers])

  useEffect(() => {
    if (!session) return
    const channel = supabase.channel('cyerp-rt')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'products' }, loadData)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'records' }, loadData)
      .subscribe(s => setRealtimeStatus(s === 'SUBSCRIBED' ? 'connected' : 'connecting'))
    return () => supabase.removeChannel(channel)
  }, [session, loadData])

  useEffect(() => {
    if (users.length === 0) return
    const loadAllModules = async () => {
      const map = {}
      for (const u of users) {
        const { data } = await supabase.from('module_permissions').select('module,enabled').eq('user_id', u.id)
        map[u.id] = data?.filter(d => d.enabled).map(d => d.module) || []
      }
      setUserModules(map)
    }
    loadAllModules()
  }, [users])

  const handleLogin = async () => {
    setLoginError('')
    const password = loginForm.password
    if (!loginForm.email || !password) { setLoginError('請填寫帳號和密碼'); return }
    // 員工用工號登入：自動轉換成 email 格式（用公司代碼區隔不同公司的相同工號）
    if (loginMode === 'employee' && !loginForm.companyCode.trim()) {
      setLoginError('請填寫公司代碼'); return
    }
    const email = loginMode === 'employee'
      ? `${loginForm.email.toLowerCase()}@${loginForm.companyCode.toLowerCase().trim()}.local`
      : loginForm.email
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) { setLoginError('工號或密碼錯誤，請聯繫管理員'); return }
    const { data: profile } = await supabase.from('user_profiles').select('approved').eq('id', data.user.id).single()
    if (profile?.approved === false) { await supabase.auth.signOut(); setLoginError('帳號尚未通過審核，請聯繫管理員') }
  }

  const handleForgotPassword = async () => {
    if (!forgotPwEmail.trim()) { setLoginError('請填寫電子郵件'); return }
    setLoginError('')
    const { error } = await supabase.auth.resetPasswordForEmail(forgotPwEmail.trim(), {
      redirectTo: `${window.location.origin}`,
    })
    if (error) { setLoginError('發送失敗：' + error.message); return }
    setForgotPwSent(true)
  }

  const handleLogout = () => supabase.auth.signOut()

  const markNotifRead = async (id) => {
    await supabase.from('notifications').update({ is_read: true }).eq('id', id)
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n))
  }

  const markAllNotifRead = async () => {
    await supabase.from('notifications').update({ is_read: true }).eq('user_id', session.user.id).eq('is_read', false)
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })))
  }

  const unreadCount = notifications.filter(n => !n.is_read).length

  const handleApprove = async (userId, name) => {
    // Get user email
    const { data: profileData } = await supabase.from('user_profiles').select('email').eq('id', userId).single()

    // Assign the admin's company to this user
    await supabase.from('user_profiles').update({
      approved: true,
      company_id: userProfile?.company_id || null,
    }).eq('id', userId)
    const modInserts = ['wms', 'production', 'shipping', 'crm', 'reports'].map(m => ({ user_id: userId, module: m, enabled: true, company_id: userProfile?.company_id || null }))
    await supabase.from('module_permissions').upsert(modInserts, { onConflict: 'user_id,module' })
    await addLog('審核通過', `通過用戶：${name}`)

    // Send email notification via Supabase Edge Function (if available)
    // Fallback: store notification in DB for user to see on login
    await supabase.from('notifications').insert({
      user_id: userId,
      title: '帳號審核通過',
      body: `您好 ${name}，您的帳號申請已通過審核，現在可以登入 YPFOPS 系統。`,
      is_read: false,
      source: '系統通知',
    }).then(() => {}).catch(() => {})

    // Try to send email via EmailJS if configured
    try {
      await fetch('https://api.emailjs.com/api/v1.0/email/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          service_id: process.env.REACT_APP_EMAILJS_SERVICE_ID || '',
          template_id: process.env.REACT_APP_EMAILJS_TEMPLATE_ID || '',
          user_id: process.env.REACT_APP_EMAILJS_PUBLIC_KEY || '',
          template_params: {
            to_email: profileData?.email || '',
            to_name: name,
            message: '您的帳號申請已通過審核，現在可以登入盛原企業線上管理系統（CY ERP）。',
            system_url: 'https://ypfops.com',
          }
        })
      })
    } catch (e) {}

    showToast(`✓ 已通過 ${name} 的申請`)
    loadUsers()
  }

  const handleReject = async (userId, name) => {
    if (!window.confirm(`確定拒絕 ${name}？`)) return
    await supabase.from('user_profiles').delete().eq('id', userId)
    await addLog('拒絕申請', `拒絕：${name}`)
    showToast(`已拒絕 ${name}`)
    loadUsers()
  }

  // ── 公司管理員發送內部通知 ──
  const handleSendNotify = async () => {
    if (!notifyForm.title.trim() || !notifyForm.body.trim()) { showToast('請填寫標題和內容', 'error'); return }
    setSendingNotify(true)
    let targets = users.filter(u => u.id !== userProfile?.id && u.approved !== false)
    if (notifyForm.targetUserId !== 'all') {
      targets = targets.filter(u => u.id === notifyForm.targetUserId)
    }
    if (!targets.length) { showToast('沒有符合的用戶', 'error'); setSendingNotify(false); return }
    const { error } = await supabase.from('notifications').insert(
      targets.map(u => ({
        user_id: u.id,
        title: notifyForm.title.trim(),
        body: notifyForm.body.trim(),
        is_read: false,
        source: userProfile?.name || '管理員',
      }))
    )
    setSendingNotify(false)
    if (error) { showToast('發送失敗：' + error.message, 'error'); return }
    await addLog('發送通知', `「${notifyForm.title}」→ ${notifyForm.targetUserId === 'all' ? `全體 ${targets.length} 人` : targets[0]?.name}`)
    showToast(`✓ 已發送通知給 ${targets.length} 位用戶`)
    setNotifyForm({ title: '', body: '', targetUserId: 'all' })
  }

  const handleUpdateRole = async (userId, role) => {
    await supabase.from('user_profiles').update({ role }).eq('id', userId)
    await addLog('變更角色', `角色更新為 ${role}`)
    showToast('✓ 已更新')
    loadUsers()
  }

  const handleDeleteUser = async (userId, name) => {
    if (!window.confirm(`確定要刪除「${name}」的帳號？此操作無法復原，該用戶將無法再登入或重新申請。`)) return
    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession()
      const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/delete-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentSession.access_token}`,
        },
        body: JSON.stringify({ userId }),
      })
      const result = await res.json()
      if (result.error) { showToast('刪除失敗：' + result.error, 'error'); return }
      await addLog('刪除帳號', `完整刪除用戶帳號：${name}`)
      showToast(`✓ 已完整刪除 ${name} 的帳號`)
      loadUsers()
    } catch (e) {
      showToast('刪除失敗，請稍後再試', 'error')
    }
  }

  const handleUpdatePosition = async (userId, position) => {
    await supabase.from('user_profiles').update({ position }).eq('id', userId)
    await addLog('變更職位', `職位更新為 ${position}`)
    showToast('✓ 職位已更新')
    loadUsers()
  }

  const handleApplyPositionDefaults = async (userId, position) => {
    const perms = DEFAULT_PERMISSIONS[position] || DEFAULT_PERMISSIONS.user
    const inserts = PERMISSION_KEYS.map(p => ({ user_id: userId, module: p.key, enabled: perms.includes(p.key) }))
    await supabase.from('module_permissions').upsert(inserts, { onConflict: 'user_id,module' })
    // Also update role based on position
    const newRole = position === 'admin' ? 'admin' : 'user'
    await supabase.from('user_profiles').update({ role: newRole }).eq('id', userId)
    await addLog('套用職位預設', `套用「${position}」預設權限`)
    showToast('✓ 已套用預設權限')
    loadUsers()
    // Reload modules for this user
    const map = { ...userModules }
    map[userId] = perms
    setUserModules(map)
  }

  const handleToggleModule = async (userId, module, current) => {
    const newVal = !current
    const existing = await supabase.from('module_permissions').select('id').eq('user_id', userId).eq('module', module).maybeSingle()
    if (existing.data) await supabase.from('module_permissions').update({ enabled: newVal }).eq('user_id', userId).eq('module', module)
    else await supabase.from('module_permissions').insert({ user_id: userId, module, enabled: newVal })
    showToast(`✓ 模組已${newVal ? '開啟 · Enabled' : '關閉 · Disabled'}`)
    loadUsers()
  }

  const isAdmin = userProfile?.role === 'admin'
  const lowStockCount = products.filter(p => p.stock <= p.safe_stock).length

  // 手機頂部 header：當前頁面資訊
  const allTabMeta = [
    { key: 'dashboard', label: '總覽', icon: '🏠' },
    ...MODULES,
    { key: 'positions', label: '職位管理', icon: '👔' },
    { key: 'users', label: '員工管理', icon: '👥' },
    { key: 'notify', label: '發送通知', icon: '📣' },
    { key: 'logs', label: '操作日誌', icon: '🗒️' },
    { key: 'more', label: '更多功能', icon: '☰' },
    { key: 'register', label: '批量建帳', icon: '✚' },
  ]
  const currentTabMeta = allTabMeta.find(t => t.key === activeTab || activeTab === `__locked__${t.key}`)
  const isAtRoot = activeTab === 'dashboard' || activeTab === 'more'
  const filteredLogs = logSearch
    ? logs.filter(l => {
        const q = logSearch.toLowerCase()
        return (l.operator || '').toLowerCase().includes(q) ||
               (l.action  || '').toLowerCase().includes(q) ||
               (l.detail  || '').toLowerCase().includes(q)
      })
    : logs
  const pagedLogs = filteredLogs.slice((logPage - 1) * PAGE_SIZE, logPage * PAGE_SIZE)
  const totalLogPages = Math.ceil(filteredLogs.length / PAGE_SIZE)

  if (isPasswordRecovery && session) return (
    <ResetPasswordScreen
      onDone={() => {
        setIsPasswordRecovery(false)
        showToast('✅ 密碼已重設，請重新登入')
        supabase.auth.signOut()
      }}
    />
  )

  if (loading && !session) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.bg, fontFamily: "'Noto Sans TC','PingFang TC',sans-serif", flexDirection: 'column', gap: 14 }}>
      <div style={{ fontSize: 44 }}>{BRAND.icon}</div>
      <div style={{ color: C.accent, fontSize: 16, fontWeight: 700 }}>{BRAND.short}</div>
      <div style={{ color: C.muted, fontSize: 12 }}>{BRAND.name}</div>
    </div>
  )

  if (!session) {
    const BG = { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #0f2044 0%, #1e3a8a 60%, #2563eb 100%)', fontFamily: "'Noto Sans TC','PingFang TC',sans-serif", padding: 20 }
    const CARD = { background: C.panel, borderRadius: 20, padding: '36px 32px', width: '100%', maxWidth: 440, boxShadow: '0 24px 64px rgba(0,0,0,0.35)' }
    const Logo = () => (
      <div style={{ textAlign: 'center', marginBottom: 28 }}>
        <div style={{ fontSize: 44, marginBottom: 8 }}>{BRAND.icon}</div>
        <div style={{ fontSize: 13, fontWeight: 900, color: C.accent, letterSpacing: 1.5, textTransform: 'uppercase' }}>{BRAND.short}</div>
        <div style={{ fontSize: 17, fontWeight: 800, color: C.text, marginTop: 4 }}>{BRAND.name}</div>
        <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>{BRAND.sub}</div>
      </div>
    )
    const ErrorBox = () => loginError ? (
      <div style={{ color: C.red, fontSize: 13, fontWeight: 700, marginBottom: 14, padding: '11px 14px', background: C.redBg, borderRadius: 10, border: `1.5px solid ${C.redBorder}` }}>{loginError}</div>
    ) : null
    const BackBtn = () => (
      <button style={{ background: 'none', border: 'none', color: C.accent, fontWeight: 700, fontSize: 13, cursor: 'pointer', padding: '4px 0', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 4 }}
        onClick={() => { setLoginMode(null); setLoginError(''); setLoginForm({ email:'', password:'' }); setForgotPwMode(false); setForgotPwSent(false); setForgotPwEmail(''); setShowPw(false) }}>
        ← 返回選擇
      </button>
    )
    // 顯示/隱藏密碼按鈕（絕對定位在輸入框右側）
    const EyeBtn = () => (
      <button type="button" onClick={() => setShowPw(s => !s)}
        style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: showPw ? C.accent : C.muted, fontSize: 18, lineHeight: 1, padding: '4px', userSelect: 'none' }}
        title={showPw ? '隱藏密碼' : '顯示密碼'}>
        {showPw ? '🙈' : '👁'}
      </button>
    )

    // ── 模式選擇 ──
    if (!loginMode) return (
      <div style={BG}>
        <div style={{ ...CARD, maxWidth: 480 }}>
          <Logo />
          <div style={{ fontSize: 14, fontWeight: 700, color: C.muted, textAlign: 'center', marginBottom: 20 }}>請選擇登入方式</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <button onClick={() => { setLoginMode('business'); setLoginError('') }} style={{ padding: '28px 16px', borderRadius: 14, border: `2px solid ${C.accent}`, background: C.accentDim, cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s' }}>
              <div style={{ fontSize: 36, marginBottom: 10 }}>🏢</div>
              <div style={{ fontSize: 15, fontWeight: 800, color: C.accent }}>企業登入</div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 4, lineHeight: 1.5 }}>管理者 / 訂閱擁有者</div>
            </button>
            <button onClick={() => { setLoginMode('employee'); setLoginError('') }} style={{ padding: '28px 16px', borderRadius: 14, border: `2px solid ${C.greenBorder}`, background: C.greenBg, cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s' }}>
              <div style={{ fontSize: 36, marginBottom: 10 }}>👤</div>
              <div style={{ fontSize: 15, fontWeight: 800, color: C.green }}>員工登入</div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 4, lineHeight: 1.5 }}>公司員工 / 一般使用者</div>
            </button>
          </div>
        </div>
      </div>
    )

    // ── 企業登入 ──
    if (loginMode === 'business') return (
      <div style={BG}>
        <div style={CARD}>
          <Logo />
          <BackBtn />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, padding: '10px 14px', background: C.accentDim, borderRadius: 10 }}>
            <span style={{ fontSize: 20 }}>🏢</span>
            <div>
              <div style={{ fontSize: 14, fontWeight: 800, color: C.accent }}>企業管理者登入</div>
              <div style={{ fontSize: 11, color: C.muted }}>僅限系統管理者 · 帳號由 YPFOPS 提供</div>
            </div>
          </div>

          {forgotPwMode ? (
            forgotPwSent ? (
              <div style={{ textAlign: 'center', padding: '10px 0' }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>📧</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: C.green, marginBottom: 8 }}>重設信件已寄出</div>
                <div style={{ fontSize: 13, color: C.muted, marginBottom: 20, lineHeight: 1.7 }}>
                  請檢查 <strong>{forgotPwEmail}</strong> 的收件匣，點選信中連結以重設密碼。
                </div>
                <button style={{ ...st.btn('default'), width: '100%' }} onClick={() => { setForgotPwMode(false); setForgotPwSent(false); setForgotPwEmail(''); setLoginError('') }}>
                  返回登入
                </button>
              </div>
            ) : (
              <>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 14 }}>🔑 忘記密碼</div>
                <label style={st.label}>電子郵件 · Email</label>
                <input style={{ ...st.input, marginBottom: 18 }} value={forgotPwEmail} placeholder="輸入您的登入 Email"
                  onChange={e => setForgotPwEmail(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleForgotPassword()} autoFocus />
                <ErrorBox />
                <button style={{ ...st.btn(), width: '100%', padding: '14px', fontSize: 15, marginBottom: 10 }} onClick={handleForgotPassword}>
                  發送重設密碼信件
                </button>
                <button style={{ background: 'none', border: 'none', color: C.accent, fontWeight: 700, fontSize: 13, cursor: 'pointer', width: '100%', padding: '6px 0' }}
                  onClick={() => { setForgotPwMode(false); setLoginError(''); setForgotPwEmail('') }}>
                  ← 返回登入
                </button>
              </>
            )
          ) : (
            <>
              <label style={st.label}>電子郵件 · Email</label>
              <input style={{ ...st.input, marginBottom: 14 }} value={loginForm.email} placeholder="admin@company.com"
                onChange={e => setLoginForm(f => ({ ...f, email: e.target.value }))}
                onKeyDown={e => e.key === 'Enter' && handleLogin()} autoFocus />
              <label style={st.label}>密碼 · Password</label>
              <div style={{ position: 'relative', marginBottom: 8 }}>
                <input style={{ ...st.input, paddingRight: 48 }} type={showPw ? 'text' : 'password'} value={loginForm.password} placeholder="輸入密碼"
                  onChange={e => setLoginForm(f => ({ ...f, password: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && handleLogin()} />
                <EyeBtn />
              </div>
              <div style={{ textAlign: 'right', marginBottom: 18 }}>
                <button style={{ background: 'none', border: 'none', color: C.accent, fontSize: 12, cursor: 'pointer', fontWeight: 600, padding: '2px 0' }}
                  onClick={() => { setForgotPwMode(true); setLoginError(''); setForgotPwEmail(loginForm.email) }}>
                  忘記密碼？
                </button>
              </div>
              <ErrorBox />
              <button style={{ ...st.btn(), width: '100%', padding: '15px', fontSize: 15, minHeight: 52 }} onClick={handleLogin}>
                登入系統
              </button>
            </>
          )}
        </div>
      </div>
    )

    // ── 員工登入 ──
    if (loginMode === 'employee') return (
      <div style={BG}>
        <div style={CARD}>
          <Logo />
          <BackBtn />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24, padding: '10px 14px', background: C.greenBg, borderRadius: 10 }}>
            <span style={{ fontSize: 20 }}>👤</span>
            <div>
              <div style={{ fontSize: 14, fontWeight: 800, color: C.green }}>員工登入</div>
              <div style={{ fontSize: 11, color: C.muted }}>使用公司代碼 + 工號 + 密碼登入</div>
            </div>
          </div>
          <label style={st.label}>公司代碼 · Company Code</label>
          <input style={{ ...st.input, marginBottom: 14, fontSize: 16, letterSpacing: 2 }} value={loginForm.companyCode}
            placeholder="由管理員提供（如：abc123）"
            onChange={e => setLoginForm(f => ({ ...f, companyCode: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '') }))}
            onKeyDown={e => e.key === 'Enter' && handleLogin()} autoFocus />
          <label style={st.label}>工號 · Employee ID</label>
          <input style={{ ...st.input, marginBottom: 14, fontSize: 16 }} value={loginForm.email}
            placeholder="輸入工號（如：A001）"
            onChange={e => setLoginForm(f => ({ ...f, email: e.target.value.trim() }))}
            onKeyDown={e => e.key === 'Enter' && handleLogin()} />
          <label style={st.label}>密碼 · Password</label>
          <div style={{ position: 'relative', marginBottom: 20 }}>
            <input style={{ ...st.input, paddingRight: 48, letterSpacing: showPw ? 2 : 6, fontSize: 20 }}
              type={showPw ? 'text' : 'password'}
              value={loginForm.password} placeholder="● ● ● ● ● ●"
              onChange={e => setLoginForm(f => ({ ...f, password: e.target.value }))}
              onKeyDown={e => e.key === 'Enter' && handleLogin()} />
            <EyeBtn />
          </div>
          <ErrorBox />
          <button style={{ ...st.btn('success'), width: '100%', padding: '16px', fontSize: 16, minHeight: 54 }} onClick={handleLogin}>
            登入
          </button>
          <div style={{ textAlign: 'center', marginTop: 16, fontSize: 12, color: C.muted }}>
            公司代碼由管理員提供，如有問題請聯繫管理員
          </div>
        </div>
      </div>
    )

    return null
  }

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.bg, fontFamily: "'Noto Sans TC','PingFang TC',sans-serif", flexDirection: 'column', gap: 12 }}>
      <div style={{ fontSize: 40 }}>☁️</div>
      <div style={{ color: C.accent, fontSize: 16, fontWeight: 700 }}>從雲端載入資料...</div>
    </div>
  )

  // Maintenance mode screen
  if (maintenanceInfo && !userProfile?.is_super_admin) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.bg, fontFamily: "'Noto Sans TC','PingFang TC',sans-serif", padding: 24 }}>
      <div style={{ ...st.card, maxWidth: 440, width: '100%', textAlign: 'center', borderColor: '#f59e0b', borderWidth: 2 }}>
        <div style={{ fontSize: 56, marginBottom: 16 }}>🔧</div>
        <div style={{ fontSize: 22, fontWeight: 900, color: '#92400e', marginBottom: 6 }}>系統維護中</div>
        <div style={{ fontSize: 14, color: C.muted, marginBottom: 4 }}>System Maintenance</div>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 12 }}>{maintenanceInfo.name}</div>
        <div style={{ fontSize: 13, color: C.sub, marginBottom: 24, lineHeight: 1.8, padding: '12px 16px', background: C.yellowBg, borderRadius: 10 }}>
          {maintenanceInfo.message}
        </div>
        <button style={{ ...st.btn('default'), width: '100%' }} onClick={() => supabase.auth.signOut()}>登出 · Sign Out</button>
      </div>
    </div>
  )

  // ── Super Admin → 專屬後台 ──
  if (userProfile?.is_super_admin) {
    return <SuperAdminPortal userProfile={userProfile} onLogout={handleLogout} />
  }

  // Force change password on first login
  if (userProfile?.must_change_password) return (
    <ForceChangePassword session={session} showToast={showToast} onDone={() => {
      setUserProfile(p => ({ ...p, must_change_password: false }))
    }} />
  )

  const renderContent = () => {
    if (activeTab === 'more') {
      const pos = POSITIONS.find(p => p.key === (userProfile?.position || (isAdmin ? 'admin' : 'user')))
      const roleLabel = isAdmin ? '👑 管理員' : userProfile?.role === 'manager' ? '👔 主管' : `👤 ${pos?.label || '一般用戶'}`
      const moreItems = [
        ...MODULES.filter(m => enabledModules.includes(m.key)).slice(3),
        ...(isAdmin ? [
          { key: 'users',    label: '員工管理', en: 'Employees',   icon: '👥' },
          { key: 'positions',label: '職位管理', en: 'Positions',   icon: '👔' },
          { key: 'notify',   label: '發送通知', en: 'Send Notify', icon: '📣' },
          { key: 'logs',     label: '操作日誌', en: 'Logs',        icon: '🗒️' },
          { key: 'register', label: '批量建帳', en: 'Batch Add',   icon: '✚' },
        ] : []),
        ...(!isAdmin && enabledModules.includes('manage_users') ? [
          { key: 'positions', label: '職位管理', en: 'Positions', icon: '👔' },
          { key: 'users', label: '員工管理', en: 'Employees', icon: '👥' },
        ] : []),
      ]
      return (
        <div>
          {/* 使用者卡 */}
          <div style={{ ...st.card, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 14, padding: '16px' }}>
            <div style={{ width: 48, height: 48, borderRadius: '50%', background: `linear-gradient(135deg,${C.accent},#0ea5e9)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 900, color: '#fff', flexShrink: 0 }}>
              {(userProfile?.name || '?')[0].toUpperCase()}
            </div>
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{userProfile?.name}</div>
              <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{roleLabel}</div>
              {userProfile?.employee_id && <div style={{ fontSize: 11, color: C.muted, marginTop: 1, fontFamily: 'monospace' }}>#{userProfile.employee_id}</div>}
            </div>
            <button onClick={() => setShowSettings(true)} style={{ width: 44, height: 44, borderRadius: 12, background: C.panel, border: `1.5px solid ${C.border}`, color: C.muted, fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              ⚙️
            </button>
          </div>

          {/* 功能格局 */}
          {moreItems.length > 0 && (
            <>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>功能模組</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginBottom: 16 }}>
                {moreItems.map(item => (
                  <button key={item.key} onClick={() => setActiveTab(item.key)}
                    style={{ background: C.panel, border: `1.5px solid ${C.border}`, borderRadius: 14, padding: '18px 12px 14px', cursor: 'pointer', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, transition: 'border-color 0.15s' }}>
                    <span style={{ fontSize: 30 }}>{item.icon}</span>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.text, lineHeight: 1.2 }}>{item.label}</div>
                    {(item.en || item.desc) && <div style={{ fontSize: 10, color: C.muted }}>{item.en || item.desc}</div>}
                  </button>
                ))}
              </div>
            </>
          )}

          {/* 備份 */}
          {(isAdmin || userProfile?.role === 'manager' || enabledModules.includes('backup_export')) && (
            <button onClick={async () => {
              if (backupState) return
              try {
                setBackupState({ current: 0, total: 15, name: '準備中...' })
                await downloadFullBackup(userProfile?.company_id, (current, total, name) => {
                  setBackupState({ current, total, name })
                })
                showToast('✓ 備份完成，已下載到本機')
              } catch (e) {
                showToast('備份失敗：' + e.message, 'error')
              } finally {
                setBackupState(null)
              }
            }} style={{ width: '100%', ...st.btn('default'), marginBottom: 10, padding: '14px', fontSize: 14, background: backupState ? '#f1f5f9' : '#f0fdf4', border: '1.5px solid #86efac', color: '#16a34a', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }} disabled={!!backupState}>
              {backupState ? (
                <div style={{ width: '100%' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>🗄️ 備份中... {backupState.current}/{backupState.total}</div>
                  <div style={{ fontSize: 11, color: '#16a34a', opacity: 0.7, marginBottom: 6 }}>{backupState.name}</div>
                  <div style={{ height: 4, background: '#dcfce7', borderRadius: 2 }}>
                    <div style={{ height: '100%', background: '#16a34a', borderRadius: 2, width: `${(backupState.current / backupState.total) * 100}%`, transition: 'width 0.3s' }} />
                  </div>
                </div>
              ) : (
                <>🗄️ 全局備份<span style={{ fontSize: 10, opacity: 0.6 }}>下載所有資料到本機</span></>
              )}
            </button>
          )}

          {/* 登出 */}
          <button onClick={handleLogout} style={{ width: '100%', ...st.btn('danger'), padding: '15px', fontSize: 15 }}>
            ⏏ 登出 · Sign Out
          </button>
        </div>
      )
    }
    // 模組存取防護：未開通模組 → 飢餓行銷頁面
    const MODULE_KEYS = ['wms','production','shipping','crm','reports','maintenance','fives','tasks','purchasing','products','attendance']
    const lockedKey = activeTab.startsWith('__locked__') ? activeTab.replace('__locked__', '') : null
    // blockedKey：直接進入沒有 enabledModules 的頁面，或是 __locked__ 前綴
    // 注意：管理員的 enabledModules 已依公司購買模組篩選，不需要 isAdmin 豁免
    const blockedKey = lockedKey || (MODULE_KEYS.includes(activeTab) && !enabledModules.includes(activeTab) ? activeTab : null)
    if (blockedKey) {
      const mod = MODULES.find(m => m.key === blockedKey)
      const isPersonalLocked = lockedModules.includes(blockedKey) // 有職位權限但公司未購買
      return (
        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:420, gap:14, padding:32, background: C.yellowBg, borderRadius:20 }}>
          <div style={{ fontSize:64 }}>{mod?.icon || '🔒'}</div>
          <div style={{ textAlign:'center' }}>
            <div style={{ fontSize:22, fontWeight:900, color:C.yellow }}>{mod?.label || '此功能'}</div>
            <div style={{ fontSize:13, color:C.yellow, fontWeight:600, marginTop:4, opacity:0.8 }}>{mod?.en} · Premium Feature</div>
          </div>
          <div style={{ padding:'20px 28px', background:C.panel, border:`2px solid ${C.yellowBorder}`, borderRadius:16, textAlign:'center', maxWidth:360, boxShadow:'0 4px 20px rgba(234,179,8,0.15)' }}>
            <div style={{ fontSize:24, marginBottom:10 }}>🚀</div>
            <div style={{ fontSize:15, fontWeight:800, color:C.yellow, marginBottom:10 }}>貴公司尚未開通此模組</div>
            <div style={{ fontSize:13, color:C.yellow, lineHeight:2, opacity:0.85 }}>
              {isPersonalLocked
                ? '您的職位已具備此模組的使用權限，但貴公司尚未購買此方案。'
                : '此功能需升級方案才能使用。'}
              <br/>
              <span style={{ fontWeight:700 }}>立即聯絡 YPFOPS 客服</span>，解鎖更多強大功能！
            </div>
            <div style={{ marginTop:14, padding:'10px 16px', background:C.yellowBg, borderRadius:10, fontSize:12, color:C.yellow, fontWeight:600, border:`1px solid ${C.yellowBorder}` }}>
              📧 ypfops@gmail.com<br/>🌐 ypfops.com
            </div>
          </div>
          <button style={{ ...st.btn('default'), padding:'10px 24px', marginTop:4 }} onClick={() => setActiveTab('dashboard')}>← 返回總覽</button>
        </div>
      )
    }

    if (activeTab === 'dashboard') return <Dashboard products={products} records={records} userProfile={userProfile} enabledModules={enabledModules} isAdmin={isAdmin} setActiveTab={setActiveTab} isMobile={isMobile} lowStockCount={lowStockCount} />
    if (activeTab === 'wms') return <WMS products={products} records={records} userProfile={userProfile} onRefresh={loadData} showToast={showToast} addLog={addLog} isMobile={isMobile} onNavigate={setActiveTab} enabledModules={enabledModules} pendingScan={pendingScan} onClearPendingScan={() => setPendingScan(null)} />
    if (activeTab === 'production') return <Production userProfile={userProfile} showToast={showToast} addLog={addLog} isMobile={isMobile} enabledModules={enabledModules} onNavigateToWMS={() => setActiveTab(enabledModules.includes('wms') ? 'wms' : '__locked__wms')} />
    if (activeTab === 'shipping') return <Shipping products={products} userProfile={userProfile} onRefresh={loadData} showToast={showToast} addLog={addLog} isMobile={isMobile} enabledModules={enabledModules} />
    if (activeTab === 'crm') return <CRM userProfile={userProfile} showToast={showToast} addLog={addLog} isMobile={isMobile} enabledModules={enabledModules} />
    if (activeTab === 'reports') return <Reports products={products} records={records} userProfile={userProfile} isMobile={isMobile} />
    if (activeTab === 'maintenance') return <Maintenance userProfile={userProfile} showToast={showToast} addLog={addLog} enabledModules={enabledModules} isMobile={isMobile} />
    if (activeTab === 'fives') return <FiveS userProfile={userProfile} showToast={showToast} addLog={addLog} isMobile={isMobile} enabledModules={enabledModules} />
    if (activeTab === 'tasks') return <Tasks userProfile={userProfile} showToast={showToast} addLog={addLog} isMobile={isMobile} enabledModules={enabledModules} />
    if (activeTab === 'purchasing') return <Purchasing products={products} userProfile={userProfile} onRefresh={loadData} showToast={showToast} addLog={addLog} enabledModules={enabledModules} isMobile={isMobile} />
    if (activeTab === 'products') return <Products products={products} userProfile={userProfile} onRefresh={loadData} showToast={showToast} addLog={addLog} enabledModules={enabledModules} isMobile={isMobile} />
    if (activeTab === 'attendance') return <Attendance userProfile={userProfile} showToast={showToast} addLog={addLog} isMobile={isMobile} />
    if (activeTab === 'super_admin') return userProfile?.is_super_admin ? <SuperAdmin userProfile={userProfile} showToast={showToast} /> : null
    if (activeTab === 'positions') {
      if (!isAdmin && !enabledModules.includes('manage_users')) return <NoPermPage onBack={() => setActiveTab('dashboard')} />
      return <PositionManager userProfile={userProfile} showToast={showToast} onRefresh={loadData} companyModules={enabledModules} />
    }
    if (activeTab === 'notify') {
      if (!isAdmin) return <NoPermPage onBack={() => setActiveTab('dashboard')} />
      // 確保 users 已載入
      if (users.length === 0) loadUsers()
      return (
        <div>
          <PageHeader title="📣 發送通知" en="Send Notification" subtitle="向公司內部用戶發送即時通知" />
          <div style={st.card}>
            <div style={{ marginBottom: 14 }}>
              <label style={st.label}>發送對象 · Target</label>
              <select style={st.input} value={notifyForm.targetUserId} onChange={e => setNotifyForm(f => ({ ...f, targetUserId: e.target.value }))}>
                <option value="all">📢 全體員工（{users.filter(u => u.id !== userProfile?.id && u.approved !== false).length} 人）</option>
                {users.filter(u => u.id !== userProfile?.id && u.approved !== false).map(u => (
                  <option key={u.id} value={u.id}>{u.name}{u.employee_id ? ` (${u.employee_id})` : ''}</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={st.label}>通知標題 *</label>
              <input style={st.input} value={notifyForm.title} placeholder="例：重要公告、出勤提醒..." onChange={e => setNotifyForm(f => ({ ...f, title: e.target.value }))} />
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={st.label}>通知內容 *</label>
              <textarea
                style={{ ...st.input, minHeight: 100, resize: 'vertical', fontFamily: 'inherit' }}
                value={notifyForm.body}
                placeholder="填寫通知詳細內容..."
                onChange={e => setNotifyForm(f => ({ ...f, body: e.target.value }))}
              />
            </div>
            <button style={{ ...st.btn(), width: '100%', padding: '14px', fontSize: 15 }} onClick={handleSendNotify} disabled={sendingNotify || !notifyForm.title.trim() || !notifyForm.body.trim()}>
              {sendingNotify ? '發送中...' : `📣 立即發送${notifyForm.targetUserId === 'all' ? `（全體 ${users.filter(u => u.id !== userProfile?.id && u.approved !== false).length} 人）` : ''}`}
            </button>
            <div style={{ marginTop: 14, padding: '12px 16px', background: C.accentDim, borderRadius: 10, fontSize: 12, color: C.muted, lineHeight: 1.8 }}>
              💡 通知會即時出現在用戶的 🔔 鈴鐺圖示<br />
              • 用戶須開啟瀏覽器推播許可才能收到手機橫幅通知<br />
              • 適合用於：工作提醒、重要公告、緊急通知
            </div>
          </div>
        </div>
      )
    }
    if (activeTab === 'logs') return isAdmin ? (
      <div>
        <PageHeader title="🗒️ 操作日誌" en="Activity Logs" subtitle={`共 ${filteredLogs.length} / ${logs.length} 筆`} />
        {/* 搜尋列 */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            style={{ ...st.input, flex: 1, minWidth: 200, maxWidth: 360 }}
            value={logSearch}
            placeholder="🔍 搜尋操作人、動作、詳細..."
            onChange={e => { setLogSearch(e.target.value); setLogPage(1) }}
          />
          {logSearch && (
            <button style={{ ...st.btn('default'), padding: '8px 14px', fontSize: 12 }} onClick={() => { setLogSearch(''); setLogPage(1) }}>
              ✕ 清除
            </button>
          )}
          <button style={{ ...st.btn('default'), padding: '8px 14px', fontSize: 12 }} onClick={loadLogs}>
            🔄 重新載入
          </button>
        </div>
        {logs.length === 0 ? (
          <div style={st.card}>
            <EmptyState icon="🗒️" text="尚無日誌，或載入失敗（請點右上角重新載入）" />
          </div>
        ) : isMobile ? (
          <div>
            {pagedLogs.map(l => (
              <div key={l.id} style={{ ...st.card, marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={st.badge('ok')}>{l.action || '-'}</span>
                  <span style={{ fontSize: 10, color: C.muted }}>{l.created_at ? new Date(l.created_at).toLocaleString('zh-TW') : '-'}</span>
                </div>
                <div style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>{l.operator || '-'}</div>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{l.detail || '-'}</div>
              </div>
            ))}
            <Pagination page={logPage} total={totalLogPages} onChange={setLogPage} />
          </div>
        ) : (
          <div style={st.card}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>{[['時間','Time'],['操作人','Operator'],['動作','Action'],['詳細','Detail']].map(([zh,en]) => <th key={zh} style={st.th}><div>{zh}</div><div style={{fontSize:9,opacity:0.45}}>{en}</div></th>)}</tr></thead>
              <tbody>{pagedLogs.length === 0 ? <tr><td colSpan={4}><EmptyState icon="🔍" text="沒有符合條件的日誌" /></td></tr> : pagedLogs.map(l => (
                <tr key={l.id}>
                  <td style={st.td}>{l.created_at ? formatDateTime(l.created_at) : '-'}</td>
                  <td style={{ ...st.td, color: C.text }}>{l.operator || '-'}</td>
                  <td style={st.td}><span style={st.badge('ok')}>{l.action || '-'}</span></td>
                  <td style={{ ...st.td, color: C.muted, maxWidth: 300, wordBreak: 'break-word' }}>{l.detail || '-'}</td>
                </tr>
              ))}</tbody>
            </table>
            <Pagination page={logPage} total={totalLogPages} onChange={setLogPage} />
          </div>
        )}
      </div>
    ) : <NoPermPage onBack={() => setActiveTab('dashboard')} />
    if (activeTab === 'users') {
      const canManage = isAdmin || enabledModules.includes('manage_users')
      if (!canManage) return <NoPermPage onBack={() => setActiveTab('dashboard')} />
      return <EmployeeManager session={session} userProfile={userProfile} showToast={showToast} addLog={addLog} enabledModules={enabledModules} isAdmin={isAdmin} companyModules={enabledModules} />
    }
    if (activeTab === 'register') {
      if (!isAdmin) return <NoPermPage onBack={() => setActiveTab('dashboard')} />
      return <BatchRegisterPage addLog={addLog} showToast={showToast} session={session} POSITIONS={POSITIONS} />
    }
    return null
  }

  // ── Watermark ──
  const watermarkText = `${userProfile?.name || ''} ${userProfile?.employee_id ? `(${userProfile.employee_id})` : ''} · ${new Date().toLocaleDateString('zh-TW')}`

  return (
    <div style={{ fontFamily: "'Noto Sans TC','PingFang TC',sans-serif", minHeight: '100vh', background: C.bg, color: C.text, paddingLeft: isMobile ? 0 : C.sidebar, paddingBottom: isMobile ? 80 : 0, position: 'relative' }}>

      {/* Watermark overlay */}
      <div style={{
        position: 'fixed', inset: 0, zIndex: 9998,
        pointerEvents: 'none', overflow: 'hidden',
        backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(`<svg xmlns='http://www.w3.org/2000/svg' width='300' height='200'><text x='50%' y='50%' text-anchor='middle' dominant-baseline='middle' font-size='13' font-family='Noto Sans TC,sans-serif' fill='rgba(0,0,0,0.06)' transform='rotate(-30, 150, 100)'>${watermarkText}</text></svg>`)}")`,
        backgroundRepeat: 'repeat',
        backgroundSize: '300px 200px',
      }} />
      {!isMobile && <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} userProfile={userProfile} realtimeStatus={realtimeStatus} onLogout={handleLogout} lowStockCount={lowStockCount} enabledModules={enabledModules} lockedModules={lockedModules} onOpenSettings={() => setShowSettings(true)} />}
      {userProfile && <AIAssistant userProfile={userProfile} isMobile={isMobile} />}
      {showSettings && <UserSettings userProfile={userProfile} onClose={() => setShowSettings(false)} showToast={showToast} onProfileUpdated={updated => { setUserProfile(updated); setShowSettings(false) }} />}
      {isMobile && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 150, background: C.sidebarBg, padding: '0 14px', height: 58, display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 2px 10px rgba(0,0,0,0.25)' }}>
          {/* 左側：返回 or Logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
            {!isAtRoot ? (
              <button onClick={() => setActiveTab('dashboard')}
                style={{ background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: 9, width: 34, height: 34, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 17, flexShrink: 0 }}>
                ←
              </button>
            ) : (
              <span style={{ fontSize: 22, flexShrink: 0 }}>{BRAND.icon}</span>
            )}
            <div style={{ overflow: 'hidden' }}>
              {isAtRoot ? (
                <>
                  <div style={{ fontSize: 14, fontWeight: 900, color: '#ffffff', lineHeight: 1.2 }}>{BRAND.short}</div>
                  <div style={{ fontSize: 10, color: C.sidebarMuted, lineHeight: 1 }}>{BRAND.sub}</div>
                </>
              ) : (
                <div style={{ fontSize: 16, fontWeight: 800, color: '#ffffff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {currentTabMeta?.icon} {currentTabMeta?.label || activeTab}
                </div>
              )}
            </div>
          </div>
          {/* 右側：同步狀態 + 通知 + 頭像 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: realtimeStatus === 'connected' ? '#4ade80' : '#fcd34d', boxShadow: realtimeStatus === 'connected' ? '0 0 6px #4ade80' : 'none' }} />
            <NotificationBell count={unreadCount} notifications={notifications} onRead={markNotifRead} onReadAll={markAllNotifRead} show={showNotifPanel} setShow={setShowNotifPanel} isMobile={true} />
            <div style={{ width: 34, height: 34, borderRadius: '50%', background: C.sidebarActive, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: '#fff', border: '2px solid rgba(255,255,255,0.2)' }}>
              {(userProfile?.name || '?')[0].toUpperCase()}
            </div>
          </div>
        </div>
      )}
      {!isMobile && (
        <div style={{ position: 'fixed', top: 16, right: 24, zIndex: 200 }}>
          <NotificationBell count={unreadCount} notifications={notifications} onRead={markNotifRead} onReadAll={markAllNotifRead} show={showNotifPanel} setShow={setShowNotifPanel} isMobile={false} />
        </div>
      )}
      {/* 維護排程橫幅 */}
      {maintenanceNotices.filter(n => !dismissedNotices.includes(n.id)).map((n, idx) => {
        const now = new Date()
        const start = new Date(n.starts_at)
        // 時間到自動視為維護中，且不會因超過 ends_at 自動解除（管理員可能延長維護時間）
        const isActive = now >= start
        const hoursAway = Math.round((start - now) / 3600000)
        const fmt = iso => new Date(iso).toLocaleString('zh-TW', { month:'numeric', day:'numeric', hour:'2-digit', minute:'2-digit' })
        const contactParts = []
        if (n.contact_name) contactParts.push(n.contact_name)
        if (n.contact_phone) contactParts.push(n.contact_phone)
        if (n.contact_email) contactParts.push(n.contact_email)
        return (
          <div key={n.id} style={{ position: 'relative', background: isActive ? '#fef3c7' : '#fff7ed', borderBottom: `2px solid ${isActive ? '#fcd34d' : '#fed7aa'}`, padding: isMobile ? '10px 14px' : '10px 32px', paddingTop: isMobile && idx === 0 ? 68 : 10, display: 'flex', alignItems: 'flex-start', gap: 12, zIndex: 100 }}>
            <span style={{ fontSize: 20, flexShrink: 0, marginTop: 1 }}>{isActive ? '🔧' : '⚠️'}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: isActive ? '#92400e' : '#9a3412', marginBottom: 2 }}>
                {isActive ? '系統維護中' : `即將維護${hoursAway > 0 ? `（${hoursAway < 24 ? `${hoursAway} 小時後` : `${Math.round(hoursAway/24)} 天後`}）` : ''}`}
                {' — '}{n.title}
              </div>
              <div style={{ fontSize: 12, color: '#78350f', lineHeight: 1.6 }}>
                🕐 {fmt(n.starts_at)} ～ {fmt(n.ends_at)}
                {n.body ? `　${n.body}` : ''}
                {contactParts.length > 0 && (
                  <span style={{ marginLeft: 8 }}>
                    　📞 如需更改時段請聯繫：<strong>{contactParts.join(' / ')}</strong>
                  </span>
                )}
              </div>
            </div>
            <button onClick={() => setDismissedNotices(p => [...p, n.id])} style={{ flexShrink: 0, background: 'none', border: 'none', color: '#b45309', fontSize: 16, cursor: 'pointer', padding: '2px 4px', marginTop: 1 }} title="關閉">✕</button>
          </div>
        )
      })}
      <div style={{ padding: isMobile ? `${maintenanceNotices.filter(n=>!dismissedNotices.includes(n.id)).length > 0 ? 8 : 68}px 14px 16px` : '28px 32px' }}>{renderContent()}</div>
      {isMobile && <BottomNav activeTab={activeTab} setActiveTab={setActiveTab} enabledModules={enabledModules} lockedModules={lockedModules} isAdmin={isAdmin} lowStockCount={lowStockCount} />}
      {toast && <div style={st.toast(toast.type)}>{toast.msg}</div>}
    </div>
  )
}

// ── Reset Password Screen（忘記密碼連結跳轉後顯示）──
function ResetPasswordScreen({ onDone }) {
  const [newPw, setNewPw] = useState('')
  const [confirmPw, setConfirmPw] = useState('')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  const handleReset = async () => {
    if (!newPw || newPw.length < 8) { setError('密碼至少 8 個字元'); return }
    if (!/[a-z]/.test(newPw) || !/[A-Z]/.test(newPw) || !/[0-9]/.test(newPw)) { setError('密碼須包含大寫字母、小寫字母及數字，例如：Abc12345'); return }
    if (newPw !== confirmPw) { setError('兩次密碼不一致'); return }
    setSaving(true)
    const { error: err } = await supabase.auth.updateUser({ password: newPw })
    setSaving(false)
    if (err) { setError('更新失敗：' + err.message); return }
    onDone()
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)', fontFamily: "'Noto Sans TC','PingFang TC',sans-serif", padding: 20 }}>
      <div style={{ background: C.panel, borderRadius: 20, padding: '44px 40px', width: '100%', maxWidth: 440, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontSize: 48, marginBottom: 10 }}>🔑</div>
          <div style={{ fontSize: 22, fontWeight: 900, color: C.text }}>重設密碼</div>
          <div style={{ fontSize: 14, color: '#64748b', marginTop: 6 }}>Reset Your Password</div>
        </div>
        <label style={{ fontSize: 13, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 6 }}>新密碼 · New Password *</label>
        <input type="password" style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: '1.5px solid #d1d5db', fontSize: 15, marginBottom: 16, boxSizing: 'border-box' }} value={newPw} placeholder="輸入新密碼（至少4個字元）" onChange={e => setNewPw(e.target.value)} />
        <label style={{ fontSize: 13, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 6 }}>確認新密碼 · Confirm Password *</label>
        <input type="password" style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: '1.5px solid #d1d5db', fontSize: 15, marginBottom: 16, boxSizing: 'border-box' }} value={confirmPw} placeholder="再輸入一次新密碼" onChange={e => setConfirmPw(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleReset()} />
        {error && <div style={{ color: '#dc2626', fontSize: 14, fontWeight: 700, marginBottom: 16, padding: '12px 16px', background: '#fef2f2', borderRadius: 10, border: '1.5px solid #fecaca' }}>{error}</div>}
        <button style={{ width: '100%', padding: '16px', borderRadius: 12, background: 'linear-gradient(135deg,#1a56db,#2563eb)', color: '#fff', fontSize: 16, fontWeight: 800, border: 'none', cursor: 'pointer', minHeight: 56 }} onClick={handleReset} disabled={saving}>
          {saving ? '更新中...' : '確認重設密碼'}
        </button>
      </div>
    </div>
  )
}

// ── Force Change Password Screen ──
// ── 無權限頁面 ────────────────────────────────────────────────────────────────
function NoPermPage({ onBack }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 360, gap: 14, padding: 32, textAlign: 'center' }}>
      <div style={{ fontSize: 56 }}>🔒</div>
      <div style={{ fontSize: 20, fontWeight: 900, color: '#1e293b' }}>權限不足</div>
      <div style={{ fontSize: 14, color: '#64748b', lineHeight: 1.8 }}>
        您的帳號沒有存取此功能的權限<br />
        <span style={{ fontSize: 12 }}>Insufficient permissions — contact your administrator</span>
      </div>
      <button onClick={onBack} style={{ marginTop: 8, padding: '10px 24px', borderRadius: 10, background: '#f1f5f9', border: '1.5px solid #e2e8f0', color: '#475569', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>← 返回總覽</button>
    </div>
  )
}

function ForceChangePassword({ session, onDone, showToast }) {
  const [newPw, setNewPw] = useState('')
  const [confirmPw, setConfirmPw] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const handleChange = async () => {
    setError('')
    if (newPw.length < 4) { setError('密碼至少需要 4 個字元'); return }
    if (newPw !== confirmPw) { setError('兩次輸入的密碼不一致'); return }
    setSaving(true)
    const { error } = await supabase.auth.updateUser({ password: newPw })
    if (error) { setError('更新失敗：' + error.message); setSaving(false); return }
    // Clear must_change_password flag
    await supabase.from('user_profiles').update({ must_change_password: false }).eq('id', session.user.id)
    setSaving(false)
    showToast('✓ 密碼已更新，歡迎使用系統！')
    onDone()
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: `linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)`, fontFamily: "'Noto Sans TC','PingFang TC',sans-serif", padding: 20 }}>
      <div style={{ background: C.panel, borderRadius: 20, padding: '44px 40px', width: '100%', maxWidth: 440, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontSize: 48, marginBottom: 10 }}>🔐</div>
          <div style={{ fontSize: 22, fontWeight: 900, color: C.text }}>首次登入請更改密碼</div>
          <div style={{ fontSize: 14, color: C.muted, marginTop: 6 }}>First Login — Please Change Your Password</div>
        </div>
        <div style={{ fontSize: 13, color: C.yellow, padding: '12px 16px', background: C.yellowBg, borderRadius: 10, border: `1.5px solid ${C.yellowBorder}`, marginBottom: 20, lineHeight: 1.7 }}>
          ⚠️ 您的帳號使用預設密碼，為了安全請立即更改。<br />密碼可以是純數字或英數字組合，至少 4 個字元。
        </div>
        <label style={st.label}>新密碼 · New Password *</label>
        <input type="password" style={{ ...st.input, marginBottom: 16 }} value={newPw} placeholder="輸入新密碼（至少4個字元）" onChange={e => setNewPw(e.target.value)} />
        <label style={st.label}>確認新密碼 · Confirm Password *</label>
        <input type="password" style={{ ...st.input, marginBottom: 16 }} value={confirmPw} placeholder="再輸入一次新密碼" onChange={e => setConfirmPw(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleChange()} />
        {error && <div style={{ color: C.red, fontSize: 14, fontWeight: 700, marginBottom: 16, padding: '12px 16px', background: C.redBg, borderRadius: 10, border: `1.5px solid ${C.redBorder}` }}>{error}</div>}
        <button style={{ ...st.btn(), width: '100%', padding: '16px', fontSize: 16, minHeight: 56 }} onClick={handleChange} disabled={saving}>
          {saving ? '更新中...' : '確認更改密碼'}
        </button>
      </div>
    </div>
  )
}

// ── Employee Management Page ──
function EmployeeManagePage({ session, userProfile, showToast, addLog }) {
  const [employees, setEmployees] = useState([])
  const [positions, setPositions] = useState([])
  const [saving, setSaving] = useState(false)
  const [showAdd, setShowAdd] = useState(false)
  const [editingUser, setEditingUser] = useState(null)
  const [name, setName] = useState('')
  const [employeeId, setEmployeeId] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [positionId, setPositionId] = useState('')
  const [department, setDepartment] = useState('')
  const [phone, setPhone] = useState('')

  const load = async () => {
    const [{ data: emps }, { data: pos }] = await Promise.all([
      supabase.from('user_profiles').select('*').order('name'),
      supabase.from('company_positions').select('*').order('sort_order'),
    ])
    if (emps) setEmployees(emps.filter(e => !e.is_super_admin))
    if (pos) setPositions(pos)
  }

  useEffect(() => { load() }, [])

  const resetForm = () => {
    setName(''); setEmployeeId(''); setEmail(''); setPassword('')
    setPositionId(''); setDepartment(''); setPhone('')
    setEditingUser(null)
  }

  const handleSave = async () => {
    if (!name.trim()) { showToast('請填寫員工姓名', 'error'); return }
    if (!editingUser && !email.trim()) { showToast('請填寫 Email', 'error'); return }
    if (!editingUser && !password.trim()) { showToast('請設定密碼', 'error'); return }
    if (!editingUser && password.length < 6) { showToast('密碼至少 6 個字元', 'error'); return }
    setSaving(true)
    try {
      if (editingUser) {
        // Update existing employee
        const posObj = positions.find(p => p.id === positionId)
        await supabase.from('user_profiles').update({
          name: name.trim(),
          employee_id: employeeId.trim(),
          department: department.trim(),
          phone: phone.trim(),
          position_id: positionId || null,
          position: posObj?.name || '',
          updated_at: new Date().toISOString(),
        }).eq('id', editingUser.id)
        await addLog('更新員工', `更新員工資料：${name}`)
        showToast('✓ 員工資料已更新')
      } else {
        // Create new employee via Supabase Edge Function
        const { data: { session: currentSession } } = await supabase.auth.getSession()
        const posObj = positions.find(p => p.id === positionId)
        const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/create-user`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${currentSession.access_token}` },
          body: JSON.stringify({
            accessToken: currentSession.access_token,
            employees: [{
              name: name.trim(),
              employee_id: employeeId.trim(),
              email: email.trim(),
              password: password,
              phone: phone.trim(),
              department: department.trim(),
              position: posObj?.name || '',
              position_id: positionId || null,
            }]
          }),
        })
        const data = await res.json()
        const success = (data.results || []).filter(r => r.success).length
        if (success > 0) {
          await addLog('新增員工', `新增員工：${name}`)
          showToast('✓ 員工帳號已建立')
        } else {
          showToast('建立失敗：' + (data.results?.[0]?.error || '未知錯誤'), 'error')
          setSaving(false); return
        }
      }
      setShowAdd(false); resetForm(); load()
    } catch (e) {
      showToast('操作失敗：' + e.message, 'error')
    }
    setSaving(false)
  }

  const handleDelete = async (id, uname) => {
    if (!window.confirm(`確定刪除員工「${uname}」的帳號？`)) return
    const { data: { session: currentSession } } = await supabase.auth.getSession()
    await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/delete-user`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${currentSession.access_token}` },
      body: JSON.stringify({ user_id: id }),
    })
    await addLog('刪除員工', `刪除員工：${uname}`)
    showToast('✓ 已刪除')
    load()
  }

  const openEdit = (emp) => {
    setEditingUser(emp)
    setName(emp.name || '')
    setEmployeeId(emp.employee_id || '')
    setEmail(emp.email || '')
    setPhone(emp.phone || '')
    setDepartment(emp.department || '')
    setPositionId(emp.position_id || '')
    setShowAdd(true)
  }

  return (
    <div>
      <PageHeader title="👥 員工管理" en="Employee Management" subtitle="新增員工、設定職位" />

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button style={st.btn()} onClick={() => { setShowAdd(!showAdd); resetForm() }}>
          <span>{showAdd ? '✕ 取消' : '+ 新增員工'}</span>
          <span style={{ fontSize: 9, opacity: 0.5, display: 'block' }}>{showAdd ? 'Cancel' : 'Add Employee'}</span>
        </button>
      </div>

      {showAdd && (
        <div style={{ ...st.card, marginBottom: 16, border: `2px solid ${C.accent}` }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: C.text, marginBottom: 16 }}>
            {editingUser ? `✏️ 編輯員工 — ${editingUser.name}` : '+ 新增員工帳號'}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
            <div>
              <label style={st.label}>姓名 · Name *</label>
              <input style={st.input} value={name} placeholder="員工姓名" onChange={e => setName(e.target.value)} />
            </div>
            <div>
              <label style={st.label}>工號 · Employee ID</label>
              <input style={st.input} value={employeeId} placeholder="EMP001" onChange={e => setEmployeeId(e.target.value)} />
            </div>
            {!editingUser && (
              <div>
                <label style={st.label}>Email * <span style={{ color: C.muted, fontWeight: 400 }}>(登入帳號)</span></label>
                <input style={st.input} value={email} placeholder="employee@company.com" onChange={e => setEmail(e.target.value)} />
              </div>
            )}
            {!editingUser && (
              <div>
                <label style={st.label}>密碼 · Password * <span style={{ color: C.muted, fontWeight: 400 }}>(至少6碼)</span></label>
                <input type="password" style={st.input} value={password} placeholder="設定初始密碼" onChange={e => setPassword(e.target.value)} />
              </div>
            )}
            <div>
              <label style={st.label}>部門 · Department</label>
              <input style={st.input} value={department} placeholder="生產部、倉儲部..." onChange={e => setDepartment(e.target.value)} />
            </div>
            <div>
              <label style={st.label}>電話 · Phone</label>
              <input style={st.input} value={phone} placeholder="0912-345-678" onChange={e => setPhone(e.target.value)} />
            </div>
            <div>
              <label style={st.label}>職位 · Position</label>
              {positions.length > 0 ? (
                <select style={st.input} value={positionId} onChange={e => setPositionId(e.target.value)}>
                  <option value="">-- 選擇職位 --</option>
                  {positions.map(p => <option key={p.id} value={p.id}>{p.name}{p.name_en ? ` · ${p.name_en}` : ''}</option>)}
                </select>
              ) : (
                <div style={{ padding: '12px 14px', background: C.yellowBg, borderRadius: 10, border: `1.5px solid ${C.yellowBorder}`, fontSize: 13, color: C.yellow }}>
                  ⚠️ 請先到「職位管理」建立職位
                </div>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button style={{ ...st.btn(), flex: 1, padding: '14px' }} onClick={handleSave} disabled={saving}>
              <span>{saving ? '儲存中...' : editingUser ? '確認更新' : '建立員工帳號'}</span>
            </button>
            <button style={{ ...st.btn('default'), padding: '14px 20px' }} onClick={() => { setShowAdd(false); resetForm() }}>取消</button>
          </div>
        </div>
      )}

      {employees.length === 0 ? (
        <div style={st.card}><EmptyState icon="👥" text="尚無員工帳號 · 點上方新增" /></div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
          {employees.map(emp => {
            const pos = positions.find(p => p.id === emp.position_id)
            return (
              <div key={emp.id} style={{ ...st.card, border: `2px solid ${pos?.color ? pos.color + '33' : C.border}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                  <div style={{ width: 42, height: 42, borderRadius: '50%', background: pos?.color ? pos.color + '22' : C.accentDim, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 700, color: pos?.color || C.accent, flexShrink: 0 }}>
                    {(emp.name || '?')[0].toUpperCase()}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 15, fontWeight: 800, color: C.text }}>{emp.name}</div>
                    <div style={{ fontSize: 12, color: C.muted }}>{emp.email}</div>
                  </div>
                  {emp.role === 'admin' && <span style={{ ...st.badge('ok'), fontSize: 11 }}>⚙️ 管理員</span>}
                </div>

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                  {emp.employee_id && <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 10, background: '#f8fafc', color: C.muted, fontFamily: 'monospace' }}>🪪 {emp.employee_id}</span>}
                  {emp.department && <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 10, background: '#f8fafc', color: C.sub }}>🏢 {emp.department}</span>}
                  {pos && <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 10, background: pos.color + '15', color: pos.color, fontWeight: 700 }}>{pos.name}</span>}
                </div>

                {emp.id !== session?.user?.id && (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button style={{ ...st.btn('warning'), flex: 1, padding: '8px 12px', fontSize: 13 }} onClick={() => openEdit(emp)}>
                      <span>✏️ 編輯</span>
                    </button>
                    <button style={{ ...st.btn('danger'), padding: '8px 12px', fontSize: 13 }} onClick={() => handleDelete(emp.id, emp.name)}>
                      <span>🗑</span>
                    </button>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── Batch Register Page ──
function BatchRegisterPage({ addLog, showToast, session, POSITIONS }) {
  const DEPARTMENTS = ['管理部', '生產部', '倉儲部', '業務部', '財務部', '維修部', '品管部', '採購部', '其他']
  const emptyRow = () => ({ name: '', employee_id: '', email: '', phone: '', position: 'user', department: '', work_area: '' })
  const [rows, setRows] = useState([emptyRow(), emptyRow(), emptyRow()])
  const [saving, setSaving] = useState(false)
  const [results, setResults] = useState([])
  const [machines, setMachines] = useState([])

  useEffect(() => {
    supabase.from('machines').select('id, name, code').order('code').then(({ data }) => data && setMachines(data))
  }, [])

  const updateRow = (i, key, val) => {
    const n = [...rows]
    n[i][key] = val
    setRows(n)
  }

  const addRow = () => setRows([...rows, emptyRow()])
  const removeRow = (i) => setRows(rows.filter((_, j) => j !== i))

  const handleRegister = async () => {
    const valid = rows.filter(r => r.name.trim() && r.employee_id.trim())
    if (valid.length === 0) { showToast('請至少填寫一筆員工資料', 'error'); return }

    setSaving(true)
    setResults([])

    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession()
      const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/create-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentSession.access_token}`,
        },
        body: JSON.stringify({ accessToken: currentSession.access_token, employees: valid }),
      })
      const data = await res.json()
      setResults(data.results || [])
      const successCount = (data.results || []).filter(r => r.success).length
      await addLog('批量註冊', `批量新增 ${successCount} 位員工帳號`)
      showToast(`✓ 成功建立 ${successCount} / ${valid.length} 個帳號`)
    } catch (e) {
      showToast('批量註冊失敗：' + e.message, 'error')
    }
    setSaving(false)
  }

  return (
    <div>
      <PageHeader title="👤 批量註冊員工" en="Batch Register Employees" subtitle="預先建立員工帳號，預設密碼 0000，首次登入須強制更改" />

      <div style={{ ...st.card, marginBottom: 16, background: C.yellowBg, border: `2px solid ${C.yellowBorder}` }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.yellow, marginBottom: 6 }}>⚠️ 注意事項</div>
        <div style={{ fontSize: 14, color: C.text, lineHeight: 1.8 }}>
          • 所有新帳號預設密碼為 <strong>0000</strong><br />
          • 員工首次登入時系統會<strong>強制要求更改密碼</strong><br />
          • Email 若留空，系統會使用「工號@公司代碼.local」作為登入帳號（例：A001@cy01.local）<br />
          • 工號為唯一識別碼，不可重複
        </div>
      </div>

      <div style={st.card}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1050 }}>
            <thead>
              <tr>
                {[['姓名 *','Name'],['工號 *','ID'],['部門','Dept'],['工作區域','Work Area'],['Email','Email'],['電話','Phone'],['職位','Position'],['']].map(([zh,en]) => (
                  <th key={zh} style={st.th}>
                    <div>{zh}</div>
                    {en && <div style={{ fontSize: 10, opacity: 0.6 }}>{en}</div>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i}>
                  <td style={st.td}>
                    <input style={{ ...st.input, minWidth: 90 }} value={row.name} placeholder="員工姓名" onChange={e => updateRow(i, 'name', e.target.value)} />
                  </td>
                  <td style={st.td}>
                    <input style={{ ...st.input, minWidth: 80 }} value={row.employee_id} placeholder="EMP001" onChange={e => updateRow(i, 'employee_id', e.target.value)} />
                  </td>
                  <td style={st.td}>
                    <select style={{ ...st.input, minWidth: 100 }} value={row.department} onChange={e => updateRow(i, 'department', e.target.value)}>
                      <option value="">-- 選擇 --</option>
                      {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </td>
                  <td style={st.td}>
                    <select style={{ ...st.input, minWidth: 120 }} value={row.work_area} onChange={e => updateRow(i, 'work_area', e.target.value)}>
                      <option value="">-- 機台 --</option>
                      {machines.map(m => <option key={m.id} value={m.code}>{m.code} · {m.name}</option>)}
                    </select>
                  </td>
                  <td style={st.td}>
                    <input style={{ ...st.input, minWidth: 150 }} value={row.email} placeholder="可留空" onChange={e => updateRow(i, 'email', e.target.value)} />
                  </td>
                  <td style={st.td}>
                    <input style={{ ...st.input, minWidth: 90 }} value={row.phone} placeholder="可留空" onChange={e => updateRow(i, 'phone', e.target.value)} />
                  </td>
                  <td style={st.td}>
                    <select style={{ ...st.input, minWidth: 120 }} value={row.position} onChange={e => updateRow(i, 'position', e.target.value)}>
                      {POSITIONS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                    </select>
                  </td>
                  <td style={st.td}>
                    {rows.length > 1 && (
                      <button style={{ ...st.btn('danger'), padding: '8px 12px', minHeight: 40, fontSize: 13 }} onClick={() => removeRow(i)}>✕</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
          <button style={{ ...st.btn('default'), padding: '10px 20px' }} onClick={addRow}>
            <span>+ 新增一列</span>
            <span style={{ fontSize: 10, opacity: 0.6, display: 'block' }}>Add Row</span>
          </button>
          <button style={{ ...st.btn(), padding: '10px 24px' }} onClick={handleRegister} disabled={saving}>
            <span>{saving ? '建立帳號中...' : `確認批量建立帳號`}</span>
            <span style={{ fontSize: 10, opacity: 0.7, display: 'block' }}>{saving ? 'Processing...' : `Batch Create ${rows.filter(r => r.name && r.employee_id).length} Accounts`}</span>
          </button>
        </div>
      </div>

      {/* Results */}
      {results.length > 0 && (
        <div style={{ ...st.card, marginTop: 16 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: C.text, marginBottom: 14 }}>建立結果 · Results</div>
          {results.map((r, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: `1px solid ${C.border}` }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{r.name} <span style={{ color: C.muted, fontSize: 13 }}>({r.employee_id})</span></div>
                {!r.success && <div style={{ fontSize: 12, color: C.red, marginTop: 3 }}>{r.error}</div>}
              </div>
              <span style={st.badge(r.success ? 'ok' : 'warn')}>{r.success ? '✓ 建立成功' : '✕ 失敗'}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function Dashboard({ products, records, userProfile, enabledModules, isAdmin, setActiveTab, isMobile, lowStockCount }) {
  const totalItems = products.reduce((a, b) => a + b.stock, 0)
  const lowStock = products.filter(p => p.stock <= p.safe_stock)
  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: C.text }}>總覽儀表板</div>
        <div style={{ fontSize: 14, color: C.muted, marginTop: 4 }}>歡迎回來，<strong style={{ color: C.accent }}>{userProfile?.name}</strong></div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 18 }}>
        {[
          { icon: '📦', value: products.length, label: '商品種類', color: C.accent, border: C.border, bg: '#fff' },
          { icon: '🗃️', value: totalItems, label: '總庫存量', color: '#0891b2', border: C.border, bg: '#fff' },
          { icon: '⚠️', value: lowStock.length, label: '庫存警示', color: lowStock.length > 0 ? C.red : C.green, border: lowStock.length > 0 ? C.redBorder : C.greenBorder, bg: lowStock.length > 0 ? C.redBg : C.greenBg },
          { icon: '📋', value: records.length, label: '異動紀錄', color: '#7c3aed', border: C.border, bg: '#fff' },
        ].map((s, i) => (
          <div key={i} style={{ background: s.bg, border: `2px solid ${s.border}`, borderRadius: 14, padding: '18px 16px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>{s.icon}</div>
            <div style={{ fontSize: 34, fontWeight: 900, color: s.color, marginBottom: 4 }}>{s.value}</div>
            <div style={{ fontSize: 14, color: C.muted, fontWeight: 600 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Low stock alert */}
      {lowStock.length > 0 && (
        <div style={{ background: C.redBg, border: `2px solid ${C.redBorder}`, borderRadius: 14, padding: '16px 20px', marginBottom: 18 }}>
          <div style={{ fontSize: 16, color: C.red, fontWeight: 800, marginBottom: 10 }}>⚠️ 庫存警示 — 以下商品需補貨</div>
          {lowStock.slice(0, 3).map(p => (
            <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${C.redBorder}` }}>
              <span style={{ color: C.text, fontSize: 15, fontWeight: 600 }}>{p.name}</span>
              <span style={{ fontSize: 14, color: C.red, fontWeight: 700 }}>現有 {p.stock} / 安全 {p.safe_stock}</span>
            </div>
          ))}
        </div>
      )}

      {/* Modules */}
      <div style={{ fontSize: 16, fontWeight: 800, color: C.text, marginBottom: 12 }}>系統模組</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 20 }}>
        {MODULES.map(m => {
          const active = enabledModules.includes(m.key)
          return (
            <div key={m.key} onClick={() => active && setActiveTab(m.key)}
              style={{ background: active ? C.panel : C.bg, border: `2px solid ${active ? C.accent : C.border}`, borderRadius: 14, textAlign: 'center', padding: isMobile ? '16px 8px' : '20px 12px', cursor: active ? 'pointer' : 'default', opacity: active ? 1 : 0.5, transition: 'all 0.15s', boxShadow: active ? '0 2px 8px rgba(26,86,219,0.12)' : 'none' }}>
              <div style={{ fontSize: isMobile ? 28 : 32, marginBottom: 8 }}>{m.icon}</div>
              <div style={{ fontSize: isMobile ? 12 : 14, fontWeight: 800, color: active ? C.text : C.muted }}>{m.label}</div>
              <div style={{ fontSize: 11, color: C.accent, fontWeight: 700, marginTop: 4 }}>{m.desc}</div>
              {!active && <div style={{ fontSize: 11, color: C.red, marginTop: 4, fontWeight: 600 }}>未開啟</div>}
            </div>
          )
        })}
      </div>

      {/* Recent transactions */}
      <div style={{ background: C.panel, border: `1.5px solid ${C.border}`, borderRadius: 16, padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: C.text, marginBottom: 14 }}>最近交易紀錄</div>
        {records.slice(0, 5).length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px', color: C.muted, fontSize: 14 }}>尚無交易紀錄</div>
        ) : records.slice(0, 5).map(r => (
          <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: `1px solid ${C.border}` }}>
            <div>
              <div style={{ fontSize: 15, color: C.text, fontWeight: 700 }}>{r.products?.name || '-'}</div>
              <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>{r.operator} · {new Date(r.created_at).toLocaleDateString('zh-TW')}</div>
            </div>
            <span style={st.badge(r.type)}>{r.type === 'in' ? `+${r.qty}` : `-${r.qty}`} {r.products?.unit}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
